import { AutocompleteOption, PoolFormikAutocomplete, PoolFormikAutocompleteProps } from '@synergy/components-next';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactNode, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { getLocalizedString } from '@synergy/utils';

import { TaxRate } from '../../models/tax-rate';
import { allTaxRatesSelector } from '../../../features/configuration/finance/tax-rates/store/tax-rates-selector';
import { fetchAllTaxRates } from '../../../features/configuration/finance/tax-rates/store/tax-rates-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export interface TaxRateAutocompleteProps
    extends Omit<
        Partial<PoolFormikAutocompleteProps>,
        'options' | 'renderValue' | 'classes' | 'onValueChange' | 'selectedLabel'
    > {
    useId?: boolean;
    display?: 'full' | 'short';
    showLabel?: boolean;
    label?: ReactNode;
    taxRates?: TaxRate[] | null;
    onValueChange?: (taxRate: TaxRate | null) => void;
}

export interface TaxRateAutocompleteRef {
    getTaxRate: (id?: number | null) => TaxRate | null;
}

export const TaxRateAutocomplete = forwardRef<TaxRateAutocompleteRef, TaxRateAutocompleteProps>((props, ref) => {
    const {
        display = 'full',
        required = true,
        showLabel = true,
        name = 'taxRateId',
        label,
        onValueChange,
        useId,
        taxRates: propsTaxRates,
        ...restProps
    } = props;
    const locales = useAppSelector(state => state.supportedLocales);
    const storeTaxRates = useAppSelector(allTaxRatesSelector);
    const dispatch = useThunkDispatch();
    const [taxRates, setTaxRates] = useState<TaxRate[] | null>(null);
    const intl = useIntl();

    useImperativeHandle(ref, () => ({
        getTaxRate: (id?: number | null) => taxRates?.find(taxRate => taxRate.id === id) ?? null,
    }));

    useEffect(() => {
        if (propsTaxRates) {
            setTaxRates(propsTaxRates);
        } else {
            setTaxRates(storeTaxRates);
        }
    }, [propsTaxRates, storeTaxRates]);

    useEffect(() => {
        if (!propsTaxRates) {
            dispatch(fetchAllTaxRates());
        }
    }, [dispatch, propsTaxRates]);

    // Short names the chosen account by its number alone, for a narrow cell;
    // the list to choose from always reads in full.
    const getDisplayValue = (value: TaxRate | number | null, length = display): string => {
        if (typeof value === 'number') {
            return `${value}`;
        }

        if (value === null || value === undefined) {
            return '';
        }

        let displayString = '';

        if (length === 'short') {
            return typeof value.counterAccount === 'number' ? value.counterAccount.toString() : ':';
        }

        if (typeof value.counterAccount === 'number') {
            displayString += `${value.counterAccount} : `;
        }

        displayString += getLocalizedString(locales, value.description);

        if (typeof value.taxPercentage === 'number') {
            displayString += ` - ${value.taxPercentage} %`;
        }

        if (typeof value.taxCode === 'number') {
            displayString += ` - ${value.taxCode}`;
        }

        return displayString;
    };

    const options: AutocompleteOption<TaxRate | number>[] = useMemo(() => {
        return (
            taxRates
                ?.sort((a, b) => {
                    const aAccount = a.counterAccount ?? Number.POSITIVE_INFINITY;
                    const bAccount = b.counterAccount ?? Number.POSITIVE_INFINITY;

                    if (aAccount !== bAccount) {
                        return aAccount - bAccount;
                    }

                    return a.id - b.id;
                })
                ?.map(taxRate => ({
                    value: useId ? taxRate.id : taxRate,
                    displayValue: getDisplayValue(taxRate, 'full'),
                })) ?? []
        );
    }, [display, locales, taxRates, useId]);

    const handleSelected = (value: TaxRate | number | null): void => {
        if (!value) {
            return;
        }

        if (typeof value === 'number') {
            const taxRate = taxRates?.find(taxRate => taxRate.id === value);
            onValueChange?.(taxRate ?? null);
        } else {
            onValueChange?.(value);
        }
    };

    const getValueFunction = (value: TaxRate | number | null): TaxRate | number | null => {
        if (!value) {
            return null;
        }

        if (typeof value === 'number') {
            return value;
        } else {
            return taxRates?.find(taxRate => taxRate.id === value.id) ?? null;
        }
    };

    let autocompleteLabel = showLabel ? label : undefined;
    autocompleteLabel ??= <FormattedMessage id="app.common.counterAccount" />;

    return (
        <PoolFormikAutocomplete<TaxRate | number>
            placeholder={intl.formatMessage({ id: 'app.common.counterAccount' })}
            label={autocompleteLabel}
            options={options}
            fullWidth={true}
            required={required}
            name={name}
            {...restProps}
            onChange={handleSelected}
            getValue={getValueFunction}
            dropdownWidth={'fit-content'}
            getDisplayValue={value => getDisplayValue(value)}
            // In a position the account number is all that is shown, and the
            // room kept for a clear button left it 30 pixels: a required
            // account is changed, not cleared. Next to the country mark the
            // cell narrows to about 70 pixels, which still has to hold four
            // digits beside the arrow.
            sx={
                'short' === display
                    ? {
                          '& .MuiAutocomplete-clearIndicator': { display: 'none' },
                          '& .MuiAutocomplete-endAdornment': { right: '4px' },
                          '&& .MuiAutocomplete-inputRoot.MuiInputBase-root': {
                              paddingLeft: '6px',
                              paddingRight: '26px',
                          },
                      }
                    : restProps.sx
            }
            selectedLabel={
                'short' === display
                    ? option => ('' === option.value ? option.displayValue : getDisplayValue(option.value))
                    : undefined
            }
        />
    );
});
